/* eslint-disable tailwindcss/no-arbitrary-value */

'use client';

import { clientCookies } from '@hyphengroup/cms-components/utils/cookies';
import { useCurrentCountry } from '@hyphengroup/cms-components/utils/translations/useCurrentCountry';
import { Button } from '@material-tailwind/react';
import {
  LeadCapturePage_attributeList,
  LeadCapturePage_offer,
  ProductWithOfferApply,
} from '__generated__/graphql';
import { useBreakpoint } from 'car-insurance/hooks/useBreakpoint';
import { VehicleModel } from 'car-insurance/types';
import React from 'react';
import { t } from 'utils/locale';
import { Translated } from 'utils/staticText';
import {
  trackApplyNow,
  trackConfirmLeadDetails,
  trackProductClickApply,
} from '../../lib/tracking';
import { ITrackingProductPageBaseParameters } from '../../lib/tracking/shared/baseParameters';
import { RedirectScreen } from '../Redirect';
import type { RedirectPageStaticText } from '../Redirect/i18n';
import {
  AuthForm,
  AuthFormProps,
  EMPLOYMENT_STATUS_VALUE_TO_LABEL,
  EXISTING_CC_VALUE_TO_LABEL,
} from './AuthForm';
import type { StickyQuickApplyTextType } from './i18n';
import { getCarInsuranceLayoutId, SendLeadToZoho } from './utils.server';
import { Provider } from '$/(application)/apply/[productId]/components/Page';
import {
  getRedirectCta,
  isInternalDomain,
  resolveProviderRedirectUrl,
  toZohoProductUid,
} from '$/(application)/apply/[productId]/components/StickyQuickApply/utils';
import {
  CatalogueProductOffers,
  getAllActiveOffers,
} from '$/(application)/components/rewardOffersUtils';
import { getTrackingParams } from '$/(application)/tracking';
import { ClippedTextStaticText } from '$/components/ClippedText/i18n';
import { CONTENT_MHG } from '$/components/cms/translations/mhg';
import { StickyFooter } from '$/components/StickyFooter';

interface StickyQuickApplyProps {
  lang: string;
  tracking: ITrackingProductPageBaseParameters;
  staticText: Translated<StickyQuickApplyTextType>;
  clippedTextStaticText: Translated<typeof ClippedTextStaticText>;
  redirectStaticText: Translated<typeof RedirectPageStaticText>;
  product: ProductWithOfferApply['getProduct'];
  offer: LeadCapturePage_offer;
  marketingConsent: boolean;
  currencyDisplay: string;
  onChangeMarketingConsent: (newValue: boolean) => void;
  attributes: LeadCapturePage_attributeList['attributeList'];
  providers?: Provider[];
  vehiclesData?: {
    makeNames: string[];
    models: VehicleModel[];
  };
}

export const StickyQuickApply: React.FC<StickyQuickApplyProps> = ({
  lang,
  product,
  offer,
  staticText,
  clippedTextStaticText,
  redirectStaticText,
  marketingConsent,
  currencyDisplay,
  onChangeMarketingConsent,
  tracking,
  providers,
  vehiclesData,
}) => {
  const [isAuthOpened, setAuthOpened] = React.useState(false);
  const [redirectUrl, setRedirectUrl] = React.useState<URL | null>(null);
  const content = CONTENT_MHG.get(lang);
  const currentCountryCode = useCurrentCountry();
  const isMobile = useBreakpoint() === 'sm';

  const handleCTAClick = () => {
    trackApplyNow(tracking);
    trackProductClickApply(tracking);
    setAuthOpened(true);
  };

  const handleSubmit: AuthFormProps['onSubmit'] = async (values) => {
    const verticalCode = product?.productUid?.split('.')[1] || 'CC';
    const mappedValues: {
      First_Name: string;
      Middle_Name: string;
      Last_Name: string;
      Email: string;
      Phone: string;
      Employment_Status: string;
      Has_Existing_CC?: string | undefined;
      Card_tenure_new?: string;
      Existing_CC_Provider_new?: string[];
      Marketing_Consent: boolean;
      Product_Type: string;
      Product_UID: string;
      Selected_Product: string | undefined;
      Selected_Provider: string | undefined;
      Selected_Offer: string | undefined;
      Monthly_Income_New_Site: number;
      Loan_Amount?: number | undefined;
      Loan_Tenure?: string | undefined;
      Lead_Status: string;
      isAutoCreated: boolean;
      Date_of_Birth: string | undefined;
      Employment_Tenure: string | undefined;
      Layout?: { id: string };
      Business_Tenure: string | undefined;
      Car_Make?: string | undefined;
      Car_Model?: string | undefined;
    } = {
      First_Name: values.firstName,
      Middle_Name: values.middleName ?? '',
      Last_Name: values.lastName,
      Email: values.email,
      Phone: values.phoneNumber,
      Employment_Status:
        EMPLOYMENT_STATUS_VALUE_TO_LABEL[values.employmentStatus] ??
        values.employmentStatus,
      Has_Existing_CC: values.hasExistingCC
        ? (EXISTING_CC_VALUE_TO_LABEL[values.hasExistingCC] ?? '')
        : '',
      Marketing_Consent: values.marketingConsent,
      Card_tenure_new: values?.CCTenure ?? '',
      Existing_CC_Provider_new: values.bankIssued ?? '',
      Product_Type: getProductType(verticalCode),
      Product_UID: toZohoProductUid(product?.productUid),
      Selected_Product: product?.productName,
      Selected_Provider: product?.provider?.providerName,
      Selected_Offer: offer?.offerTitle ?? '',
      Monthly_Income_New_Site: values.monthlyIncome,
      Loan_Amount: values.loanAmount,
      Loan_Tenure: values.loanTenure ?? '',
      Lead_Status: 'New Lead',
      isAutoCreated: true,
      Date_of_Birth: values.dateOfBirth ?? '',
      Employment_Tenure: values.employmentTenure ?? '',
      Business_Tenure: values.businessOperatingDuration ?? '',
      Car_Make: values.vehicleMake ?? '',
      Car_Model: values.vehicleModel ?? '',
    };

    // Despite the name this list is not "banking verticals" — it is the set of
    // verticals that use Zoho's default layout. Anything missing here is given
    // the car-insurance layout, so a new vertical must be added or its leads
    // land in the wrong CRM layout. BBB (CORE-4728) uses the default layout.
    const defaultZohoLayoutVerticals = ['CC', 'PL', 'BL', 'CL', 'ALTL', 'BBB'];
    if (!defaultZohoLayoutVerticals.includes(verticalCode)) {
      mappedValues.Layout = {
        id: await getCarInsuranceLayoutId(),
      };
    }
    try {
      const url = new URL(window.location.href);
      const trackingParams = getTrackingParams(
        clientCookies(),
        url.searchParams.toString(),
      );
      const leadParamsFromUrl = {
        UtmSource: trackingParams.utm_source?.toString() ?? '',
        UtmMedium: trackingParams.utm_medium?.toString() ?? '',
        UtmCampaign: trackingParams.utm_campaign?.toString() ?? '',
        UtmChannel: trackingParams.utm_channel?.toString() ?? '',
        UtmContent: trackingParams.utm_content?.toString() ?? '',
        UtmStrategy: trackingParams.utm_strategy?.toString() ?? '',
        UtmTerm: trackingParams.utm_term?.toString() ?? '',
        scaleo_click_id: trackingParams.clkid?.toString() ?? '',
        scaleo_click_type: trackingParams.clktype?.toString() ?? '',
      };
      const { leadId } = await SendLeadToZoho(
        {
          ...mappedValues,
          ...leadParamsFromUrl,
        },
        { productUid: product?.productUid ?? undefined },
      );
      if (!Array.isArray(product?.cta?.links))
        throw new Error(`No CTA data to redirect to`);
      const redirectCTA = getRedirectCta(product?.cta?.links, isMobile);
      const withLeadId = leadId
        ? redirectCTA?.redirectedUrl?.replace('{lead_id}', leadId)
        : redirectCTA?.redirectedUrl;
      const redirectUrl = resolveProviderRedirectUrl({
        redirectLink: withLeadId ?? '',
        productId: product?.productUid,
        currentHref: window.location.href,
      });
      setRedirectUrl(redirectUrl);
    } catch (e) {
      console.error(e);
    }
    trackConfirmLeadDetails(tracking);
  };

  if (redirectUrl) {
    if (isInternalDomain(redirectUrl.toString())) {
      window.location.href = redirectUrl.toString();
      return null;
    }

    return (
      <RedirectScreen
        product={product as ProductWithOfferApply['getProduct']}
        redirectUrl={redirectUrl}
        staticText={redirectStaticText}
        offer={offer}
      />
    );
  }

  const verticalCode = product?.productUid?.split('.')[1] || 'CC';
  const hasReward =
    getAllActiveOffers(product as CatalogueProductOffers).length > 0;

  return (
    <StickyFooter>
      <div className="mhg-alpha-box-shadow-2 px-[--spacing-padding-x] py-[--spacing-padding-y] bg-[--fill-card]">
        <div className="mx-auto flex flex-col items-center justify-between gap-[--spacing-gap-y-sm] md:max-w-[--general-narrow-content-max-width-2] md:flex-row lg:max-w-[--general-narrow-content-max-width-3] lg:gap-[--spacing-gap-x-sm]">
          <div className="mhg-alpha-title-1 text-[--text-primary]">
            {t(hasReward ? staticText.content : staticText.contentNoReward, {
              verticalName: getVerticalLabel(verticalCode, hasReward),
            })}
          </div>
          <Button
            variant="filled"
            fullWidth
            className="inline-flex items-center gap-2 bg-buttons/cta text-text/on-dark md:max-w-110"
            onClick={handleCTAClick}
          >
            {content.apply_now}
          </Button>
        </div>
      </div>

      <AuthForm
        staticText={staticText}
        isOpen={isAuthOpened}
        setOpen={setAuthOpened}
        onSubmit={handleSubmit}
        tracking={tracking}
        currentCountryCode={currentCountryCode}
        currencyDisplay={currencyDisplay}
        marketingConsent={marketingConsent}
        onChangeMarketingConsent={onChangeMarketingConsent}
        clippedTextStaticText={clippedTextStaticText}
        productUid={product?.productUid}
        attributes={product?.attributeList}
        providers={providers}
        vehiclesData={vehiclesData}
        hasReward={hasReward}
      />
    </StickyFooter>
  );
};

/**
 * BBB is SME broadband — the first non-financial vertical. The code is `BBB`
 * rather than `BB` because `BB` is already taken by broadband in another MHG
 * market. `CVG` in `BBB.CVG.*` is the provider, Converge.
 *
 * TODO(CORE-4732): confirm the exact Zoho `Product_Type` picklist label. The
 * mapping document was not available when this was written, so the value is
 * isolated here as a one-line change — and so BBB does not silently fall
 * through to `'Unknown Product Type'` in the CRM meanwhile. If Zoho validates
 * `Product_Type` as a picklist, a wrong label is rejected at lead creation.
 */
export const BBB_PRODUCT_TYPE = 'SME Broadband';

export function getProductType(verticalCode: string): string {
  switch (verticalCode) {
    case 'PL':
      return 'Personal Loan';
    case 'CC':
      return 'Credit Card';
    case 'LFI':
      return 'Life Insurance';
    case 'BL':
      return 'Business Loan';
    case 'CL':
      return 'Car Loan';
    case 'ALTL':
      return 'Alternative Lending';
    case 'BBB':
      return BBB_PRODUCT_TYPE;
    default:
      return 'Unknown Product Type';
  }
}

export function getVerticalLabel(
  verticalCode: string,
  hasReward = true,
): string {
  switch (verticalCode) {
    case 'PL':
      return 'Loan';
    case 'CC':
      return hasReward ? 'Card & Reward' : 'Card';
    case 'LFI':
      return 'Insurance';
    case 'BL':
      return 'Business Loan';
    case 'ALTL':
      return 'Loan';
    case 'BBB':
      return 'Broadband Plan';
    default:
      return 'Application';
  }
}
